<template>
    <div class="pb-6">
        <div class="wizzard-form">
            <div class="label-header" ref="steps">
                <button
                    class="step-label"
                    @click="() => jumpToStep(1)"
                    :class="step == 1 ? 'is-active' : (lowerThanPrevStep(1) ? 'completed' : '')"
                    data-step
                    v-ripple
                    :disabled="!lowerThanPrevStep(1)"
                >
                    <i class="material-icons">tune</i>
                    <span>Informácie o projekte</span>
                </button>
                <span class="step-label-divider">
                    <i class="material-icons">navigate_next</i>
                </span>
                <button
                    class="step-label"
                    :class="step == 2 ? 'is-active' : (lowerThanPrevStep(2) ? 'completed' : '')"
                    @click="() => jumpToStep(2)"
                    data-step
                    v-ripple
                    :disabled="!lowerThanPrevStep(2)"
                >
                    <i class="material-icons">person_outline</i>
                    <span>Výber klienta</span>
                </button>
                <span class="step-label-divider">
                    <i class="material-icons">navigate_next</i>
                </span>
                <button
                    class="step-label"
                    :class="step == 3 ? 'is-active' : (lowerThanPrevStep(3) ? 'completed' : '')"
                    @click="() => jumpToStep(3)"
                    data-step
                    v-ripple
                    :disabled="!lowerThanPrevStep(3)"
                >
                    <i class="material-icons">people_outline</i>
                    <span>Členovia a nastavenia</span>
                </button>
                <span class="step-label-divider">
                    <i class="material-icons">navigate_next</i>
                </span>
                <button
                    class="step-label"
                    :class="step == 4 ? 'is-active' : (lowerThanPrevStep(4) ? 'completed' : '')"
                    @click="() => jumpToStep(4)"
                    data-step
                    v-ripple
                    :disabled="!lowerThanPrevStep(4)"
                >
                    <i class="material-icons">info_outline</i>
                    <span>Prehľad a potvrdenie</span>
                </button>
                <span class="progress-bar">
                    <span class="bar-fill" :style="{width: ((this.step / this.steps) * 100) + '%'}"></span>
                </span>
            </div>
            <div class="step-body">
                <div class="step-content step-1" :key="'step1'" v-show="step == 1">
                    <floating-label
                        focus-color="orange"
                        label="Nazov projektu"
                        :required="true"
                        v-model="project.name"
                        name="name"
                        :validation="projectNameValidator"
                    />
                    <floating-label
                        focus-color="orange"
                        label="Kratky popis"
                        :optional="true"
                        v-model="project.slogan"
                        name="slogan"
                    />
                    <floating-label
                        focus-color="orange"
                        :optional="true"
                        type="textarea"
                        label="Popis"
                        name="description"
                        v-model="project.description"
                    />
                </div>
                <div class="step-content step-2" :key="'step2'" v-show="step == 2">
                    <label class="text-gray-600 -ml-8">
                        <input type="radio" v-model="client.type" name="type" value="actual" />
                        <span class="ml-2">S klientom sme uz spolupracovali:</span>
                    </label>
                    <div class="my-6">
                        <multiselect
                            :disabled="client.type !== 'actual'"
                            v-model="client.id"
                            :allow-empty="true"
                            :options="clients"
                            track-by="id"
                            label="full_name"
                            selectLabel="Enterom potvrdiť"
                            selectedLabel="Vybrané"
                            deselectLabel="Enterom zrušiť"
                            placeholder="Vyberte možnosť"
                        ></multiselect>
                    </div>
                    <label class="text-gray-600 -ml-8">
                        <input type="radio" v-model="client.type" name="type" value="new" />
                        <span class="ml-2">Tento projekt bude pre noveho klienta</span>
                    </label>
                    <div>
                        <floating-label
                            :disabled="client.type === 'actual'"
                            label="Meno"
                            :required="client.type !== 'actual'"
                            v-model="client.name"
                            :validation="clientNameValidator"
                        />
                        <floating-label
                            :disabled="client.type === 'actual'"
                            label="Priezvisko"
                            :required="client.type !== 'actual'"
                            v-model="client.lastname"
                            :validation="clientLastNameValidator"
                        />
                        <floating-label
                            :disabled="client.type === 'actual'"
                            label="Telefon"
                            :required="client.type !== 'actual'"
                            v-model="client.phone"
                            :validation="clientPhoneValidator"
                        />
                        <floating-label
                            :disabled="client.type === 'actual'"
                            label="E-mail"
                            :optional="true"
                            v-model="client.email"
                            :validation="clientEmailValidator"
                        />
                        <floating-label
                            :disabled="client.type === 'actual'"
                            label="Firma"
                            :optional="true"
                            v-model="client.company"
                        />
                        <floating-label
                            :disabled="client.type === 'actual'"
                            label="Fakturacna adresa"
                            :optional="true"
                            v-model="client.invoice_address"
                        />
                    </div>
                </div>
                <div class="step-content step-3" :key="'step3'" v-show="step == 3">
                    <label class="text-gray-400 text-xs">Pociatocny stav *</label>
                    <multiselect
                        v-model="settings.status"
                        :selected="1"
                        :allow-empty="false"
                        :options="statuses"
                        track-by="id"
                        label="name"
                        selectLabel="Enterom potvrdiť"
                        selectedLabel="Vybrané"
                        deselectLabel="Enterom zrušiť"
                        placeholder="Vyberte možnosť"
                    ></multiselect>

                    <label class="text-gray-400 text-xs mt-6">Clenovia (volitelne)</label>
                    <multiselect
                        v-model="settings.members"
                        :options="users"
                        deselect-label="Can't remove this value"
                        :searchable="true"
                        :close-on-select="false"
                        :multiple="true"
                        :show-labels="false"
                        :hideSelected="true"
                        track-by="id"
                        label="name"
                        placeholder="Vyber členov projektu"
                    >
                        <template slot="singleLabel" slot-scope="{ option }">
                            {{ option.name }} ({{
                            option.type.name }})
                        </template>

                        <template slot="tag" slot-scope="{ option, remove }">
                            <div class="multiselect__tag" v-ripple>
                                <img
                                    v-if="option.image"
                                    :src="'/uploads/'+option.image"
                                    class="w-6 h-6 rounded-full mr-3"
                                />
                                <span
                                    v-if="!option.image"
                                    :class="'bg-'+option.color"
                                    class="inicial-box flex items-center justify-center mr-3 text-xs w-6 h-6 text-white rounded-full"
                                >{{ getInicials(option.name, option.image) }}</span>
                                <span class="text-sm">{{ option.name }}</span>
                                <span class="multiselect__tag-icon">
                                    <i class="material-icons" @click="remove(option)">close</i>
                                </span>
                            </div>
                        </template>

                        <template slot="option" slot-scope="{ option }">
                            <span class="flex items-center">
                                <img
                                    v-if="option.image"
                                    :src="'/uploads/'+option.image"
                                    class="w-6 h-6 rounded-full mr-3"
                                />
                                <span
                                    v-if="!option.image"
                                    :class="'bg-'+option.color"
                                    class="inicial-box flex items-center justify-center mr-3 text-xs w-6 h-6 text-white rounded-full"
                                >{{ getInicials(option.name, option.image) }}</span>
                                <span class="text-sm">{{ option.name }} ({{ option.type.name }})</span>
                            </span>
                        </template>
                    </multiselect>

                    <floating-label label="Domena" :optional="true" v-model="settings.domain" />
                </div>
                <div class="step-content step-4" :key="'step4'" v-show="step == 4">
                    <h6 class="text-lg mb-2">Informácie o projekte</h6>
                    <div class="flex flex-col mb-4">
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">Nazov:</span>
                            <span class="text-left w-3/4 pl-3">{{ project.name }}</span>
                        </span>
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">Podnadpis:</span>
                            <span class="text-left w-3/4 pl-3">{{ project.slogan }}</span>
                        </span>
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">Popis:</span>
                            <span class="text-left w-3/4 pl-3">{{ project.description }}</span>
                        </span>
                    </div>
                    <h6>Výber klienta</h6>
                    <div class="flex flex-col mb-4" v-if="client.type === 'new'">
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">Meno:</span>
                            <span class="text-left w-3/4 pl-3">{{ client.name }}</span>
                        </span>
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">Priezvisko:</span>
                            <span class="text-left w-3/4 pl-3">{{ client.lastname }}</span>
                        </span>
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">Telefon:</span>
                            <span class="text-left w-3/4 pl-3">{{ client.phone }}</span>
                        </span>
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">E-mail:</span>
                            <span class="text-left w-3/4 pl-3">{{ client.email }}</span>
                        </span>
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">Firma:</span>
                            <span class="text-left w-3/4 pl-3">{{ client.company }}</span>
                        </span>
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">Fakturacna adresa:</span>
                            <span class="text-left w-3/4 pl-3">{{ client.invoice_address }}</span>
                        </span>
                    </div>
                    <div class="flex flex-col mb-4" v-if="client.type === 'actual' && client.id">
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">Meno:</span>
                            <span class="text-left w-3/4 pl-3">{{ client.id.name }}</span>
                        </span>
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">Priezvisko:</span>
                            <span class="text-left w-3/4 pl-3">{{ client.id.lastname }}</span>
                        </span>
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">Telefon:</span>
                            <span class="text-left w-3/4 pl-3">{{ client.id.phone }}</span>
                        </span>
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">E-mail:</span>
                            <span class="text-left w-3/4 pl-3">{{ client.id.email }}</span>
                        </span>
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">Firma:</span>
                            <span class="text-left w-3/4 pl-3">{{ client.id.company }}</span>
                        </span>
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">Fakturacna adresa:</span>
                            <span class="text-left w-3/4 pl-3">{{ client.id.invoice_address }}</span>
                        </span>
                    </div>
                    <h6>Členovia a nastavenia</h6>
                    <div class="flex flex-col mb-4">
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">Pociatocny stav:</span>
                            <span
                                class="text-left w-3/4 pl-3"
                                :class="'text-'+settings.status.color+'-400'"
                            >{{ settings.status.name }}</span>
                        </span>
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">Clenovia:</span>
                            <span class="text-left w-3/4 pl-3">
                                <span
                                    class="bg-gray-100 text-black rounded-full inline-flex items-center justify-center pl-2 pr-3 py-1 mb-3 text-sm"
                                    v-for="option in settings.members"
                                    :key="'sm'+option.id"
                                >
                                    <img
                                        v-if="option.image"
                                        :src="'/uploads/'+option.image"
                                        class="w-6 h-6 rounded-full mr-3"
                                    />
                                    <span
                                        v-if="!option.image"
                                        :class="'bg-'+option.color"
                                        class="inicial-box flex items-center justify-center mr-3 text-xs w-6 h-6 text-white rounded-full"
                                    >{{ getInicials(option.name, option.image) }}</span>
                                    <span class="text-xs">{{ option.name }}</span>
                                </span>
                            </span>
                        </span>
                        <span class="py-2 flex text-sm text-gray-600 border-b border-gray-100">
                            <span class="text-right w-1/4">Domena:</span>
                            <span class="text-left w-3/4 pl-3">{{ settings.domain }}</span>
                        </span>
                    </div>
                </div>
            </div>
            <div class="step-footer">
                <button
                    v-ripple
                    class="secondary"
                    @click="() => jumpToStep(prevStep)"
                    :disabled="step === prevStep"
                >Spat</button>
                <button
                    v-ripple
                    v-if="this.step !== this.steps"
                    class="primary"
                    @click="() => jumpToStep(nextStep)"
                    :disabled="currentValidatorResult"
                >Pokracovat</button>
                <button
                    v-ripple
                    v-if="this.step === this.steps"
                    class="primary"
                    @click="postForm"
                    :disabled="currentValidatorResult"
                >Odoslat</button>
            </div>
        </div>
    </div>
</template>

<script>
import FloatingLabel from "../../components/FloatingLabel";
import { mapState } from "vuex";
import { validateEmail, getInicials } from "../../utils";
export default {
    name: "ProjectWizzard",

    components: { FloatingLabel },

    beforeMount() {
        this.$store.dispatch("fetchClients");
        this.$store.dispatch("fetchUsers");
        this.$store.dispatch("fetchStatuses").then(() => {
            if (this.statuses.length === 0) return;
            let firstStatus = this.statuses[0];
            this.settings.status = Object.assign({}, firstStatus);
        });
    },

    data: () => ({
        step: 1,
        steps: 0,

        project: {},
        client: {
            type: "actual"
        },
        settings: {
            status: {},
            members: []
        }
    }),

    computed: {
        ...mapState({
            clients: state => state.clients.all,
            users: state => state.users.all,
            statuses: state => state.statuses.all
        }),
        prevStep() {
            let prev = this.step - 1;
            if (prev < 1) prev = 1;
            return prev;
        },

        nextStep() {
            let next = this.step + 1;
            if (next > this.steps) next = this.steps;
            return next;
        },

        currentValidatorResult() {
            let functionNameBuild = "validatorStep" + "" + this.step;
            let functionName = functionNameBuild;

            if (typeof this[functionName] === "undefined") {
                console.error(functionName + " not found!");
                return true;
            }

            return !this[functionName]();
        }
    },

    mounted() {
        let children = this.$refs.steps.children;

        for (let child of children) {
            if ("data-step" in child.attributes) this.steps += 1;
        }
    },

    methods: {
        getInicials(n, i) {
            return getInicials(n, i);
        },
        jumpToStep(step = 1) {
            this.step = step;
        },

        lowerThanPrevStep(step = 1) {
            let cond = this.step > step - 1;
            return cond;
        },

        validatorStep1() {
            if (
                Object.keys(this.project).length > 0 &&
                this.project.name.length >= 3
            ) {
                return true;
            }

            return false;
        },
        validatorStep2() {
            if (
                this.client.type === "actual" &&
                typeof this.client.id !== "undefined" &&
                this.client.id !== null
            ) {
                return true;
            }

            if (this.client.type === "new") {
                if (
                    !this.client.name ||
                    !this.client.lastname ||
                    !this.client.phone
                ) {
                    return false;
                }

                if (
                    this.client.name.length < 2 ||
                    this.client.lastname.length < 2 ||
                    this.client.phone < 7
                ) {
                    return false;
                }

                if (this.client.email && this.client.email.length >= 1) {
                    return validateEmail(this.client.email);
                }

                return true;
            }

            return false;
        },
        validatorStep3() {
            return true;
        },
        validatorStep4() {
            return true;
        },

        postForm() {
            let object = Object.assign({}, this.project);
            if (this.client.type === "actual") object.client = this.client.id;
            else {
                object.client = this.client;
            }

            let members = [];
            for (let member of this.settings.members) {
                members.push(member.id);
            }

            object.domain = this.settings.domain;
            object.members = members;
            object.status_id = this.settings.status.id;

            console.log(object);
            this.$store.dispatch("postProject", object).then(() => {
                this.$router.push({
                    name: "projects.list"
                });
            });
        },

        /// inputs validation
        projectNameValidator() {
            return (
                typeof this.project.name !== "undefined" &&
                this.project.name.length >= 3
            );
        },
        clientNameValidator() {
            return this.client.name.length >= 2;
        },
        clientLastNameValidator() {
            return this.client.lastname.length >= 2;
        },
        clientPhoneValidator() {
            return this.client.phone.length >= 7;
        },
        clientEmailValidator() {
            if (this.client.email.length >= 1) {
                return validateEmail(this.client.email);
            }
            return true;
        }
    }
};
</script>

